import {z} from "zod/v4-mini"
import type {BuilderInlet} from "#template/runtime-graph/graph-builder"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {NodeEvaluator} from "#template/node-evaluator"
import {createGeometryAttributeData} from "#template/nodes/geometry-graph/interfaces"
import type {GeometryAttributeData} from "#template/nodes/geometry-graph/interfaces"
import {GeometryAttributeNode, meshGeometryAttributeLike} from "#template/nodes/geometry-graph/attribute/attribute-node"
import type {MeshGeometryAttributeLikeNode} from "#template/nodes/geometry-graph/attribute/attribute-node"
import type {NonNullableParameters} from "#template/nodes/geometry-graph/common/geometry-graph-node"
import type {NumberLike} from "#template/nodes/types"
import {numberLike} from "#template/nodes/number/fundamental"
import type {AttributeRef} from "#template/geometry-processing/geometry-graph"
import {applyGeometryOperator} from "#template/nodes/geometry-graph/common/geometry-graph-node-declaration"

// Single-variant operator (mesh-only): parameter type is concrete, no per-dim genericity.
export type NormalsOperatorParameters = {
    name: string
    positionAttribute: MeshGeometryAttributeLikeNode | null
    smoothingAttribute: MeshGeometryAttributeLikeNode | NumberLike | null
}

export const normalsOperatorParametersSchema = z.object({
    name: z.string(),
    positionAttribute: z.nullable(meshGeometryAttributeLike),
    smoothingAttribute: z.nullable(z.union([meshGeometryAttributeLike, numberLike])),
})

function applyNormalsOperator(
    positionAttribute: GeometryAttributeData<2>,
    smoothingAttribute: GeometryAttributeData<2> | number | undefined,
): GeometryAttributeData<2> {
    const positionAttrRefData = positionAttribute.ref
    const smoothingAttrRefData = smoothingAttribute === undefined || typeof smoothingAttribute === "number" ? smoothingAttribute : smoothingAttribute.ref

    const normalsAttrRefData = applyGeometryOperator(
        (positionAttrRef: AttributeRef, smoothingAttrRef: AttributeRef | number | undefined) => positionAttrRef.normals(smoothingAttrRef),
        positionAttrRefData,
        smoothingAttrRefData,
    )

    return createGeometryAttributeData(normalsAttrRefData, positionAttribute.dim)
}

export abstract class GeometryNormalsOperatorNode<out P extends NormalsOperatorParameters = NormalsOperatorParameters> extends GeometryAttributeNode<2, P> {
    protected override hasValidParameters(parameters: P): parameters is P & NonNullableParameters<P> {
        return parameters.positionAttribute !== null
    }

    protected override setupGeometryData(
        parameters: NonNullableParameters<P>,
        scope: GraphBuilderScope,
        evaluator: NodeEvaluator,
    ): {evaluatedGeometryData: BuilderInlet<GeometryAttributeData<2> | null>; invalidator?: BuilderInlet<null>} {
        const {positionAttribute, smoothingAttribute} = parameters

        const [positionAttributeDataValid, positionAttributeDataInvalid] = scope.branch(evaluator.evaluate(positionAttribute))
        const smoothingAttributeData = smoothingAttribute !== null ? evaluator.evaluate(smoothingAttribute) : null

        const geometryAttributeData = scope.pureLambda(
            scope.tuple(positionAttributeDataValid, smoothingAttributeData),
            ([positionAttributeData, smoothingAttributeData]) =>
                applyNormalsOperator(positionAttributeData, smoothingAttributeData === null ? undefined : smoothingAttributeData),
            "applyNormalsOperator",
        )

        return {evaluatedGeometryData: geometryAttributeData, invalidator: positionAttributeDataInvalid}
    }
}
